// =====================================================================
// Bank name picker — type to search, click to select.
//
// Bank name used to be a free-text box, so a typo ("HDFC Bnak", "state bank")
// was saved as-is and later broke the bank upload / payment steps. This field
// only accepts a name from INDIAN_BANKS. When a valid IFSC is known, the bank
// is suggested from its 4-letter prefix (see bankNameForIfsc).
// =====================================================================

import { SearchableCombobox } from "./ui/SearchableCombobox";

// Official names as the banks / RBI list them. `ifsc` is the 4-letter IFSC
// prefix, used to fill the name from an IFSC code.
export const INDIAN_BANKS: ReadonlyArray<{ name: string; ifsc?: string }> = [
  // Public sector banks
  { name: "Bank of Baroda", ifsc: "BARB" },
  { name: "Bank of India", ifsc: "BKID" },
  { name: "Bank of Maharashtra", ifsc: "MAHB" },
  { name: "Canara Bank", ifsc: "CNRB" },
  { name: "Central Bank of India", ifsc: "CBIN" },
  { name: "Indian Bank", ifsc: "IDIB" },
  { name: "Indian Overseas Bank", ifsc: "IOBA" },
  { name: "Punjab & Sind Bank", ifsc: "PSIB" },
  { name: "Punjab National Bank", ifsc: "PUNB" },
  { name: "State Bank of India", ifsc: "SBIN" },
  { name: "UCO Bank", ifsc: "UCBA" },
  { name: "Union Bank of India", ifsc: "UBIN" },
  // Private sector banks
  { name: "Axis Bank", ifsc: "UTIB" },
  { name: "Bandhan Bank", ifsc: "BDBL" },
  { name: "CSB Bank", ifsc: "CSBK" },
  { name: "City Union Bank", ifsc: "CIUB" },
  { name: "DCB Bank", ifsc: "DCBL" },
  { name: "Dhanlaxmi Bank", ifsc: "DLXB" },
  { name: "Federal Bank", ifsc: "FDRL" },
  { name: "HDFC Bank", ifsc: "HDFC" },
  { name: "ICICI Bank", ifsc: "ICIC" },
  { name: "IDBI Bank", ifsc: "IBKL" },
  { name: "IDFC FIRST Bank", ifsc: "IDFB" },
  { name: "IndusInd Bank", ifsc: "INDB" },
  { name: "Jammu & Kashmir Bank", ifsc: "JAKA" },
  { name: "Karnataka Bank", ifsc: "KARB" },
  { name: "Karur Vysya Bank", ifsc: "KVBL" },
  { name: "Kotak Mahindra Bank", ifsc: "KKBK" },
  { name: "Nainital Bank", ifsc: "NTBL" },
  { name: "RBL Bank", ifsc: "RATN" },
  { name: "South Indian Bank", ifsc: "SIBL" },
  { name: "Tamilnad Mercantile Bank", ifsc: "TMBL" },
  { name: "YES Bank", ifsc: "YESB" },
  // Small finance banks
  { name: "AU Small Finance Bank", ifsc: "AUBL" },
  { name: "Capital Small Finance Bank", ifsc: "CLBL" },
  { name: "Equitas Small Finance Bank", ifsc: "ESFB" },
  { name: "ESAF Small Finance Bank", ifsc: "ESMF" },
  { name: "Jana Small Finance Bank", ifsc: "JSFB" },
  { name: "North East Small Finance Bank", ifsc: "NESF" },
  { name: "Shivalik Small Finance Bank" },
  { name: "Suryoday Small Finance Bank", ifsc: "SURY" },
  { name: "Ujjivan Small Finance Bank", ifsc: "UJVN" },
  { name: "Unity Small Finance Bank" },
  { name: "Utkarsh Small Finance Bank", ifsc: "UTKS" },
  // Payments banks
  { name: "Airtel Payments Bank", ifsc: "AIRP" },
  { name: "Fino Payments Bank", ifsc: "FINO" },
  { name: "India Post Payments Bank", ifsc: "IPOS" },
  { name: "Jio Payments Bank", ifsc: "JIOP" },
  { name: "NSDL Payments Bank", ifsc: "NSPB" },
  { name: "Paytm Payments Bank", ifsc: "PYTM" },
  // Foreign banks
  { name: "Barclays Bank", ifsc: "BARC" },
  { name: "Citibank", ifsc: "CITI" },
  { name: "DBS Bank India", ifsc: "DBSS" },
  { name: "Deutsche Bank", ifsc: "DEUT" },
  { name: "HSBC", ifsc: "HSBC" },
  { name: "Standard Chartered Bank", ifsc: "SCBL" },
  // Co-operative banks
  { name: "Abhyudaya Co-operative Bank", ifsc: "ABHY" },
  { name: "Bharat Co-operative Bank (Mumbai)", ifsc: "BCBM" },
  { name: "Cosmos Co-operative Bank", ifsc: "COSB" },
  { name: "Kalupur Commercial Co-operative Bank", ifsc: "KCCB" },
  { name: "Saraswat Co-operative Bank", ifsc: "SRCB" },
  { name: "SVC Co-operative Bank", ifsc: "SVCB" },
  { name: "TJSB Sahakari Bank", ifsc: "TJSB" },
];

export const INDIAN_BANK_NAMES: string[] = INDIAN_BANKS.map((b) => b.name).sort((a, b) =>
  a.localeCompare(b),
);

/** True when `name` is exactly one of the listed banks (or empty). */
export const isKnownBankName = (name: string | null | undefined) =>
  !name || INDIAN_BANK_NAMES.includes(name.trim());

/** Bank name for an IFSC code's 4-letter prefix, or null if unknown. */
export function bankNameForIfsc(ifsc: string | null | undefined): string | null {
  const prefix = (ifsc || "").trim().toUpperCase().slice(0, 4);
  if (prefix.length < 4) return null;
  return INDIAN_BANKS.find((b) => b.ifsc === prefix)?.name ?? null;
}

interface BankNameComboboxProps {
  value: string;
  onChange: (name: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

export function BankNameCombobox({
  value,
  onChange,
  disabled = false,
  placeholder = "Select bank",
  className,
}: BankNameComboboxProps) {
  const known = isKnownBankName(value);
  return (
    <div className={className}>
      <SearchableCombobox
        value={value}
        onChange={onChange}
        options={INDIAN_BANK_NAMES}
        placeholder={placeholder}
        searchPlaceholder="Type bank name…"
        emptyText="No bank found. Check the spelling."
        disabled={disabled}
        matchTriggerWidth
        // BankDetailsModal is a z-[1000] overlay; keep the list above it.
        contentClassName="z-[1100]"
      />
      {!known && (
        <p className="text-xs text-red-500 mt-1">
          “{value}” is not a recognised bank name. Please select it from the list.
        </p>
      )}
    </div>
  );
}
